import Microgame from '../engine/Microgame.js';

export default class CleanGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Nettoie !`, en: `Clean!`, es: `¡Limpia!` },
            controls: { fr: `Frotte l'écran vigoureusement avec ton doigt (ou la souris)`, en: `Rub the screen vigorously with your finger (or mouse)`, es: `Frota la pantalla vigorosamente con tu dedo (o ratón)` },
            objective: { fr: `Enlève toute la saleté avant la fin du temps`, en: `Remove all the dirt before time runs out`, es: `Elimina toda la suciedad antes de que se agote el tiempo` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" stroke-width="2"><path d="M22 17v1c0 .5-.5 1-1 1H3c-.5 0-1-.5-1-1v-1"/><path d="M12 4v8"/><path d="M8 8h8"/></svg>`,
            actions: [
                { type: 'swipe', fromX: 20, fromY: 30, toX: 80, toY: 30, duration: 260 },
                { type: 'swipe', fromX: 80, fromY: 48, toX: 20, toY: 48, duration: 260 },
                { type: 'swipe', fromX: 20, fromY: 66, toX: 80, toY: 66, duration: 260 },
                { type: 'wait', duration: 380 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.canvas = null;
        this.ctx = null;
        this.width = 0;
        this.height = 0;
        this.isCleaning = false;
        this.lastPoint = null;
        this.moveCount = 0;

        // Vitesse adoucie : la vitesse max (4.0) donnera le même temps que l'ancien 3.5
        const speedMult = engine.speedMultiplier || 1.0;
        const adjustedSpeed = 1 + (speedMult - 1) * (2.5 / 3);
        this.duration = 3000 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "CLEAN!";

        this.width = this.container.clientWidth;
        this.height = this.container.clientHeight;

        // Background (The Reward)
        const bg = document.createElement('div');
        bg.style.position = 'absolute';
        bg.style.top = '0';
        bg.style.left = '0';
        bg.style.width = '100%';
        bg.style.height = '100%';
        bg.style.background = 'radial-gradient(circle at center, #1a1a1a 0%, #000 100%)';
        bg.style.display = 'flex';
        bg.style.justifyContent = 'center';
        bg.style.alignItems = 'center';

        // Neon Star
        const star = document.createElement('div');
        star.textContent = '★';
        star.style.fontSize = '120px';
        star.style.color = this.primaryColor; // Primary Neon
        star.style.textShadow = `0 0 20px ${this.primaryColor}, 0 0 40px ${this.primaryColor}, 0 0 60px ${this.primaryColor}`;
        star.style.filter = `drop-shadow(0 0 10px ${this.primaryColor})`;
        bg.appendChild(star);

        this.container.insertBefore(bg, this.canvas);

        // Create Canvas (The Dirt)
        this.canvas = document.createElement('canvas');
        this.canvas.width = this.width;
        this.canvas.height = this.height;
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.zIndex = '10';
        this.container.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        // Fill with "dirt" (Tinted)
        this.ctx.fillStyle = '#111'; // Dark base
        this.ctx.fillRect(0, 0, this.width, this.height);

        // Tint with secondary color
        this.ctx.globalAlpha = 0.2;
        this.ctx.fillStyle = this.secondaryColor;
        this.ctx.fillRect(0, 0, this.width, this.height);
        this.ctx.globalAlpha = 1.0;

        // Add texture
        for (let i = 0; i < 100; i++) {
            const x = Math.random() * this.width;
            const y = Math.random() * this.height;
            const size = Math.random() * 50 + 20;
            this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'; // Darker spots
            this.ctx.beginPath();
            this.ctx.arc(x, y, size, 0, Math.PI * 2);
            this.ctx.fill();
        }

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    onDown(e) {
        if (!this.active) return;
        this.isCleaning = true;
        const rect = this.canvas.getBoundingClientRect();
        this.lastPoint = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        this.clean(this.lastPoint.x, this.lastPoint.y);
    }

    onMove(e) {
        if (!this.active || !this.isCleaning) return;
        const rect = this.canvas.getBoundingClientRect();
        const currentPoint = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };

        this.cleanLine(this.lastPoint, currentPoint);
        this.lastPoint = currentPoint;

        // Check progress periodically (every 10 moves)
        this.moveCount++;
        if (this.moveCount % 10 === 0) {
            this.checkProgress();
        }
    }

    onUp() {
        this.isCleaning = false;
        this.checkProgress();
    }

    cleanLine(p1, p2) {
        const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
        for (let i = 0; i < dist; i += 3) {
            const x = p1.x + Math.cos(angle) * i;
            const y = p1.y + Math.sin(angle) * i;
            this.clean(x, y);
        }
    }

    clean(x, y) {
        this.ctx.globalCompositeOperation = 'destination-out';
        this.ctx.beginPath();
        this.ctx.arc(x, y, 45, 0, Math.PI * 2);
        this.ctx.fill();
    }

    checkProgress() {
        const imageData = this.ctx.getImageData(0, 0, this.width, this.height);
        const data = imageData.data;
        let transparentPixels = 0;
        const sampleRate = 10;
        let totalSamples = 0;

        for (let i = 3; i < data.length; i += 4 * sampleRate) {
            totalSamples++;
            // Check for transparency (alpha < 128 is safe enough for 'cleaned')
            if (data[i] < 128) {
                transparentPixels++;
            }
        }

        const percentage = transparentPixels / totalSamples;

        if (percentage > 0.50) { // 50% threshold
            this.win();
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
